<template>
  <div class="bk-shell" :class="{ 'is-sidebar-collapsed': collapsed }">
    <aside class="bk-sidebar" :aria-expanded="(!collapsed).toString()">
      <div class="bk-sidebar-top">
        <div class="bk-sidebar-brand-row">
          <router-link to="/dashboard" class="bk-sidebar-brand" v-tooltip.right="{ value: productName, disabled: !collapsed }">
            <span class="bk-sidebar-lockup">
              <span class="bk-sidebar-wordmark">{{ productName }}</span>
              <span class="bk-sidebar-rule" aria-hidden="true" />
              <span class="bk-sidebar-parent">{{ parentName }}</span>
            </span>
            <span class="bk-sidebar-wordmark-short" aria-hidden="true">{{ productMark }}</span>
          </router-link>
        </div>

        <nav class="bk-nav" aria-label="Primary">
          <router-link
            v-for="item in primaryNav"
            :key="item.to"
            :to="item.to"
            class="bk-nav-item"
            :class="{ 'is-active': isActive(item.to) }"
            v-tooltip.right="{ value: item.label, disabled: !collapsed }"
          >
            <i :class="['pi', item.icon]" aria-hidden="true" />
            <span class="bk-nav-label">{{ item.label }}</span>
            <span
              v-if="item.to === '/jobs' && jobsCount !== null"
              class="bk-nav-badge"
              :class="{ 'is-live': jobsCount > 0 }"
            >{{ jobsCount }}</span>
          </router-link>
        </nav>
      </div>

      <div class="bk-sidebar-sources">
        <div class="bk-section-head">
          <span class="bk-section-label">Sources</span>
          <router-link
            to="/config"
            class="bk-section-action"
            v-tooltip.right="{ value: 'Add / edit feeds', disabled: false }"
          >
            <i class="pi pi-plus" aria-hidden="true" />
          </router-link>
        </div>
        <div class="bk-sidebar-sources-scroll">
          <FetchSources v-slot="{ sources }">
            <SourcesList :sources="sources" :compact="collapsed" />
          </FetchSources>
        </div>
      </div>

      <div class="bk-sidebar-plan">
        <div class="bk-section-head">
          <span class="bk-section-label">Plan</span>
        </div>
        <nav class="bk-nav" aria-label="Plan">
          <router-link
            to="/roadmap"
            class="bk-road-sign"
            :class="{ 'is-active': isActive('/roadmap') }"
            v-tooltip.right="{ value: 'Brokurz Road', disabled: !collapsed }"
            aria-label="Brokurz Road"
          >
            <span class="bk-road-sign-shield" aria-hidden="true">
              <span class="bk-road-sign-route">01</span>
            </span>
            <span class="bk-road-sign-copy">
              <span class="bk-road-sign-kicker">Scenic route</span>
              <span class="bk-road-sign-name">Brokurz Road</span>
            </span>
          </router-link>
        </nav>
      </div>

      <div class="bk-sidebar-footer">
        <nav class="bk-nav bk-nav--secondary" aria-label="Secondary">
          <router-link
            v-for="item in secondaryNav"
            :key="item.to"
            :to="item.to"
            class="bk-nav-item"
            :class="{ 'is-active': isActive(item.to) }"
            v-tooltip.right="{ value: item.label, disabled: !collapsed }"
          >
            <i :class="['pi', item.icon]" aria-hidden="true" />
            <span class="bk-nav-label">{{ item.label }}</span>
          </router-link>
        </nav>

        <div class="bk-parent-lockup" :hidden="collapsed">
          <span class="bk-parent-lockup-product">{{ productDomain }}</span>
          <span class="bk-parent-lockup-parent">a Brokurz company</span>
        </div>

        <div v-if="authEmail" class="user-box">
          <div class="user-avatar" aria-hidden="true" v-tooltip.right="{ value: authEmail, disabled: !collapsed }">
            {{ userInitial }}
          </div>
          <div class="user-meta">
            <div class="user-email" :title="authEmail">{{ authEmail }}</div>
            <div class="user-role">{{ authRole }}</div>
          </div>
          <Button
            icon="pi pi-sign-out"
            class="p-button-text p-button-sm p-button-rounded user-signout"
            v-tooltip.top="'Sign out'"
            aria-label="Sign out"
            @click="signOut"
          />
        </div>
      </div>

      <button
        type="button"
        class="bk-sidebar-edge"
        :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :title="collapsed ? 'Expand' : 'Collapse'"
        @click="toggleSidebar"
      >
        <i :class="['pi', collapsed ? 'pi-chevron-right' : 'pi-chevron-left']" aria-hidden="true" />
      </button>
    </aside>

    <div class="bk-main">
      <ApiHealthBanner />
      <header class="bk-navbar">
        <h1 class="bk-navbar-title" :class="{ 'is-road': pageMeta.isRoad }">
          <span v-if="pageMeta.isRoad" class="bk-navbar-road-badge" aria-hidden="true">
            <span class="bk-navbar-road-route">01</span>
          </span>
          <i v-else :class="['pi', pageMeta.icon]" aria-hidden="true" />
          <span>{{ pageMeta.title }}</span>
        </h1>
        <div class="tw-flex tw-items-center tw-gap-2">
          <Chip v-if="runningLabel" :label="runningLabel" />
        </div>
      </header>
      <main class="bk-content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import FetchSources from './FetchSources'
import SourcesList from './SourcesList'
import ApiHealthBanner from './ApiHealthBanner'
import { authState } from '../lib/auth'
import settings from '../settings'
import { PRODUCT_NAME, PRODUCT_MARK, PARENT_NAME, PRODUCT_DOMAIN } from '../lib/brand'

const SIDEBAR_KEY = 'sidebarCollapsed'

function readCollapsed() {
  try {
    const saved = settings.getSettings()
    if (saved && typeof saved[SIDEBAR_KEY] === 'boolean') return saved[SIDEBAR_KEY]
  } catch (e) {
    /* ignore */
  }
  return false
}

function writeCollapsed(value) {
  try {
    const saved = settings.getSettings() || {}
    settings.saveSettings({ ...saved, [SIDEBAR_KEY]: value })
  } catch (e) {
    /* ignore */
  }
}

export default {
  inject: ['jobsProvider'],
  props: {
    sourceName: String,
  },
  components: {
    FetchSources,
    SourcesList,
    ApiHealthBanner,
  },
  data() {
    return {
      collapsed: readCollapsed(),
      productName: PRODUCT_NAME,
      productMark: PRODUCT_MARK,
      parentName: PARENT_NAME,
      productDomain: PRODUCT_DOMAIN,
    }
  },
  methods: {
    isActive(routePath) {
      return this.$route.path === routePath || this.$route.path.startsWith(routePath + '/')
    },
    toggleSidebar() {
      this.collapsed = !this.collapsed
      writeCollapsed(this.collapsed)
    },
    async signOut() {
      await authState.signOut()
      this.$router.replace('/login')
    },
  },
  computed: {
    authEmail() {
      return authState.email
    },
    authRole() {
      return authState.role
    },
    userInitial() {
      const e = this.authEmail || '?'
      return e.charAt(0).toUpperCase()
    },
    primaryNav() {
      return [
        { to: '/dashboard', label: 'Dashboard', icon: 'pi-home' },
        { to: '/jobs', label: 'Jobs', icon: 'pi-briefcase' },
        { to: '/cron', label: 'Schedules', icon: 'pi-calendar' },
        { to: '/boards', label: 'Boards', icon: 'pi-map' },
        { to: '/applications', label: 'Applications', icon: 'pi-inbox' },
        { to: '/inquiries', label: 'Inquiries', icon: 'pi-envelope' },
        { to: '/vendors', label: 'Vendors', icon: 'pi-building' },
        { to: '/config', label: 'Config', icon: 'pi-cog' },
      ]
    },
    secondaryNav() {
      const items = [
        { to: '/docs', label: 'Docs', icon: 'pi-book' },
        { to: '/settings', label: 'Settings', icon: 'pi-sliders-h' },
      ]
      if (authState.isSuperAdmin) {
        items.push({ to: '/admins', label: 'Operators', icon: 'pi-users' })
      }
      return items
    },
    jobsCount() {
      if (!this.jobsProvider || this.jobsProvider.loading || this.jobsProvider.error) return null
      return this.jobsProvider.runningJobs.length
    },
    runningLabel() {
      if (this.jobsCount === null) return ''
      if (this.jobsCount === 0) return 'Idle'
      return `${this.jobsCount} running`
    },
    pageMeta() {
      const path = this.$route.path
      if (path.startsWith('/jobs')) return { title: 'Jobs', icon: 'pi-briefcase' }
      if (path.startsWith('/dashboard')) return { title: 'Home', icon: 'pi-home' }
      if (path.startsWith('/cron')) return { title: 'Schedules', icon: 'pi-calendar' }
      if (path.startsWith('/config')) return { title: 'Config', icon: 'pi-cog' }
      if (path.startsWith('/boards')) return { title: 'MLS Boards', icon: 'pi-map' }
      if (path.startsWith('/applications')) return { title: 'MLS Applications', icon: 'pi-inbox' }
      if (path.startsWith('/inquiries')) return { title: 'Inquiries', icon: 'pi-envelope' }
      if (path.startsWith('/vendors')) return { title: 'MLS Vendors', icon: 'pi-building' }
      if (path.startsWith('/settings')) return { title: 'Settings', icon: 'pi-sliders-h' }
      if (path.startsWith('/docs')) return { title: 'Documentation', icon: 'pi-book' }
      if (path.startsWith('/roadmap')) return { title: 'Brokurz Road', icon: 'pi-map', isRoad: true }
      if (path.startsWith('/admins')) return { title: 'Operators', icon: 'pi-users' }
      if (path.startsWith('/sources/')) {
        const name = path.split('/')[2] || 'Source'
        return { title: name, icon: 'pi-database' }
      }
      return { title: PRODUCT_NAME, icon: 'pi-home' }
    },
  },
}
</script>

<style scoped>
.user-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.4rem 0.35rem 0.4rem 0.45rem;
  border: 1px solid var(--bk-line);
  border-radius: var(--bk-radius-sm);
  background: #fff;
  transition: padding 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.user-avatar {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 6px;
  background: var(--bk-ink);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.user-meta {
  min-width: 0;
  flex: 1;
  opacity: 1;
  max-width: 12rem;
  overflow: hidden;
  transition: opacity 0.18s ease, max-width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.user-email {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bk-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bk-muted);
  line-height: 1.2;
}

.user-signout {
  flex-shrink: 0;
  width: 1.7rem !important;
  height: 1.7rem !important;
  opacity: 1;
  transition: opacity 0.18s ease;
}
</style>
